"use client"

import { useState, useEffect } from "react"
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Battery, BatteryCharging, Download, RefreshCw, AlertTriangle } from "react-feather"
import Card from "./ui/Card"
import Button from "./ui/Button"
import Progress from "./ui/Progress"
import "../styles/Storage.css"

// Mock data
const batteryLevelData = [
  { time: "00:00", level: 75 },
  { time: "01:00", level: 72 },
  { time: "02:00", level: 68 },
  { time: "03:00", level: 65 },
  { time: "04:00", level: 62 },
  { time: "05:00", level: 60 },
  { time: "06:00", level: 58 },
  { time: "07:00", level: 55 },
  { time: "08:00", level: 60 },
  { time: "09:00", level: 68 },
  { time: "10:00", level: 75 },
  { time: "11:00", level: 82 },
  { time: "12:00", level: 88 },
  { time: "13:00", level: 92 },
  { time: "14:00", level: 95 },
  { time: "15:00", level: 90 },
  { time: "16:00", level: 85 },
  { time: "17:00", level: 80 },
  { time: "18:00", level: 75 },
  { time: "19:00", level: 70 },
  { time: "20:00", level: 80 },
  { time: "21:00", level: 85 },
  { time: "22:00", level: 82 },
  { time: "23:00", level: 78 },
]

const powerFlowData = [
  { time: "00:00", solar: 0, grid: 2, battery: -1 },
  { time: "01:00", solar: 0, grid: 1.8, battery: -0.8 },
  { time: "02:00", solar: 0, grid: 1.5, battery: -0.5 },
  { time: "03:00", solar: 0, grid: 1.3, battery: -0.3 },
  { time: "04:00", solar: 0, grid: 1.2, battery: -0.2 },
  { time: "05:00", solar: 0.2, grid: 1, battery: 0 },
  { time: "06:00", solar: 1.5, grid: 0, battery: 0 },
  { time: "07:00", solar: 3.2, grid: 0, battery: 1.1 },
  { time: "08:00", solar: 5.8, grid: 0, battery: 3.5 },
  { time: "09:00", solar: 7.9, grid: 0, battery: 5.4 },
  { time: "10:00", solar: 9.3, grid: 0, battery: 6.5 },
  { time: "11:00", solar: 10.2, grid: 0, battery: 7.2 },
  { time: "12:00", solar: 10.8, grid: 0, battery: 7.6 },
  { time: "13:00", solar: 10.5, grid: 0, battery: 7.5 },
  { time: "14:00", solar: 9.8, grid: 0, battery: 7 },
  { time: "15:00", solar: 8.5, grid: 0, battery: 5.8 },
  { time: "16:00", solar: 6.9, grid: 0, battery: 4.0 },
  { time: "17:00", solar: 4.2, grid: 0, battery: 0.7 },
  { time: "18:00", solar: 2.1, grid: 2.1, battery: 0 },
  { time: "19:00", solar: 0.8, grid: 3.7, battery: 0 },
  { time: "20:00", solar: 0, grid: 2, battery: 2 },
  { time: "21:00", solar: 0, grid: 1.5, battery: 2 },
  { time: "22:00", solar: 0, grid: 1, battery: 2 },
  { time: "23:00", solar: 0, grid: 0.5, battery: 2 },
]

const batteryHealthData = [
  { month: "Jan", health: 100 },
  { month: "Feb", health: 99 },
  { month: "Mar", health: 99 },
  { month: "Apr", health: 98 },
  { month: "May", health: 98 },
  { month: "Jun", health: 97 },
  { month: "Jul", health: 97 },
  { month: "Aug", health: 96 },
  { month: "Sep", health: 96 },
  { month: "Oct", health: 95 },
  { month: "Nov", health: 95 },
  { month: "Dec", health: 94 },
]

const cycleData = [
  { month: "Jan", cycles: 22 },
  { month: "Feb", cycles: 20 },
  { month: "Mar", cycles: 25 },
  { month: "Apr", cycles: 23 },
  { month: "May", cycles: 26 },
  { month: "Jun", cycles: 28 },
  { month: "Jul", cycles: 30 },
  { month: "Aug", cycles: 29 },
  { month: "Sep", cycles: 27 },
  { month: "Oct", cycles: 24 },
  { month: "Nov", cycles: 21 },
  { month: "Dec", cycles: 19 },
]

function Storage() {
  const [batteryLevel, setBatteryLevel] = useState(78)
  const [batteryStatus, setBatteryStatus] = useState("Discharging")
  const [powerFlow, setPowerFlow] = useState({
    solar: 0,
    grid: 1.2,
    battery: -0.8,
    home: 2.0,
  })

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate battery level changes
      setBatteryLevel((prevLevel) => {
        const change = Math.random() > 0.5 ? 1 : -1
        const newLevel = Math.min(100, Math.max(0, prevLevel + change))

        // Update battery status based on level change
        if (change > 0) {
          setBatteryStatus("Charging")
        } else if (change < 0) {
          setBatteryStatus("Discharging")
        } else {
          setBatteryStatus("Idle")
        }

        return newLevel
      })

      // Simulate power flow changes
      setPowerFlow((prev) => {
        const hour = new Date().getHours()
        const isDaytime = hour >= 6 && hour <= 18

        let solar = isDaytime ? (Math.random() * 10).toFixed(1) : 0
        solar = Number.parseFloat(solar)

        let battery
        let grid

        if (solar > 2) {
          // Excess solar, charge battery
          battery = (Math.random() * 5).toFixed(1)
          grid = 0
        } else {
          // Not enough solar, use grid and maybe battery
          battery = Math.random() > 0.5 ? -(Math.random() * 2).toFixed(1) : 0
          grid = (Math.random() * 3).toFixed(1)
        }

        battery = Number.parseFloat(battery)
        grid = Number.parseFloat(grid)

        const home = Number.parseFloat((solar + grid - battery).toFixed(1))

        return { solar, grid, battery, home }
      })
    }, 5000) // Update every 5 seconds

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="storage-page">
      <div className="page-header">
        <h1>Battery Storage</h1>
        <div className="header-actions">
          <Button variant="outline">
            <Download size={16} />
            <span>Export</span>
          </Button>
          <Button variant="icon">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      <div className="battery-status-cards">
        <Card className="battery-status-card">
          <div className="battery-icon-container">
            {batteryStatus === "Charging" ? (
              <BatteryCharging size={48} className="battery-icon charging" />
            ) : (
              <Battery size={48} className="battery-icon" />
            )}
          </div>
          <div className="battery-info">
            <h2>Battery Status</h2>
            <div className="battery-level">
              <div className="level-value">{batteryLevel}%</div>
              <div className="level-status">{batteryStatus}</div>
            </div>
            <div className="battery-progress">
              <Progress value={batteryLevel} max={100} className="battery-progress-bar" />
            </div>
          </div>
        </Card>

        <Card className="power-flow-card">
          <h2>Current Power Flow</h2>
          <div className="power-flow-diagram">
            <div className="power-source">
              <div className="source-icon solar"></div>
              <div className="source-label">Solar</div>
              <div className="source-value">{powerFlow.solar} kW</div>
            </div>
            <div className="power-source">
              <div className="source-icon grid"></div>
              <div className="source-label">Grid</div>
              <div className="source-value">{powerFlow.grid} kW</div>
            </div>
            <div className="power-source">
              <div className="source-icon battery"></div>
              <div className="source-label">Battery</div>
              <div className="source-value">
                {powerFlow.battery > 0 ? `+${powerFlow.battery}` : powerFlow.battery} kW
              </div>
            </div>
            <div className="power-source">
              <div className="source-icon home"></div>
              <div className="source-label">Home</div>
              <div className="source-value">{powerFlow.home} kW</div>
            </div>
          </div>
        </Card>

        <Card className="battery-details-card">
          <h2>Battery Details</h2>
          <div className="battery-details">
            <div className="detail-item">
              <div className="detail-label">Capacity</div>
              <div className="detail-value">13.5 kWh</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Max Power</div>
              <div className="detail-value">5 kW</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Efficiency</div>
              <div className="detail-value">92%</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Temperature</div>
              <div className="detail-value">32°C</div>
            </div>
          </div>
        </Card>

        <Card className="battery-health-card">
          <h2>Battery Health</h2>
          <div className="health-indicator">
            <div className="health-value">94%</div>
            <div className="health-label">Good</div>
          </div>
          <div className="health-details">
            <div className="detail-item">
              <div className="detail-label">Total Cycles</div>
              <div className="detail-value">294</div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Expected Life</div>
              <div className="detail-value">~8 years</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="storage-charts">
        <Card className="chart-card full-width">
          <div className="card-header">
            <h3>Battery Level (24 Hours)</h3>
            <p className="card-description">Battery charge level throughout the day</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={batteryLevelData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis domain={[0, 100]} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Area type="monotone" dataKey="level" name="Battery Level" stroke="#3b82f6" fill="#93c5fd" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card full-width">
          <div className="card-header">
            <h3>Power Flow (24 Hours)</h3>
            <p className="card-description">Energy flow between solar, grid, and battery</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={powerFlowData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="solar" name="Solar" stroke="#f59e0b" fill="#fcd34d" stackId="1" />
                <Area type="monotone" dataKey="grid" name="Grid" stroke="#6b7280" fill="#d1d5db" stackId="1" />
                <Area type="monotone" dataKey="battery" name="Battery" stroke="#10b981" fill="#a7f3d0" stackId="1" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card">
          <div className="card-header">
            <h3>Battery Health Trend</h3>
            <p className="card-description">Battery health over the past year</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={batteryHealthData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis domain={[90, 100]} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Line type="monotone" dataKey="health" name="Health" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card">
          <div className="card-header">
            <h3>Monthly Charge Cycles</h3>
            <p className="card-description">Number of charge cycles per month</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={cycleData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="cycles" name="Cycles" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="alerts-section">
        <Card className="alerts-card">
          <div className="card-header with-icon">
            <AlertTriangle className="alert-icon" />
            <h3>Battery Alerts</h3>
          </div>
          <div className="alerts-list">
            <div className="alert-item">
              <div className="alert-time">2023-12-15 08:23</div>
              <div className="alert-message">Battery temperature exceeded 35°C</div>
              <div className="alert-status resolved">Resolved</div>
            </div>
            <div className="alert-item">
              <div className="alert-time">2023-12-10 14:45</div>
              <div className="alert-message">Battery discharge rate too high</div>
              <div className="alert-status resolved">Resolved</div>
            </div>
            <div className="alert-item">
              <div className="alert-time">2023-11-28 22:12</div>
              <div className="alert-message">Grid connection lost, switched to battery backup</div>
              <div className="alert-status resolved">Resolved</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Storage

